import {
  filterAreaRatesByAreas,
  getAllAwards,
  getAreaBasedPayData,
  getAreaListForABP,
  getAwardListByEmploymentType,
  getBasePayRule,
  getBlankRates,
  getExternalRequestPayload,
  getPayRateType,
  getPayRules,
  getRequestObjectCustomAward,
  getRequestObjectFixedRate,
  getRequestObjectHourly,
  getRequestObjectHourly40AndOvertime,
  getRequestObjectHourly44AndOvertime,
  getRequestObjectLibraryAward,
  getRequestObjectRatesPerDay,
  getSalaryRequestPayload,
  getSortedPayRules,
  initializeEmployeeContract,
  RequestObject,
  RequestObjectCustomAward,
  RequestObjectCustomAwardV2,
  getSelectedDays,
} from '@/modules/people/composables/pay-rates';
import { payrollIdForSave } from '@/modules/people/utils/payroll-id-validation';
import { isPayrollIdValidationActive } from '@/modules/people/store/uk-payroll-gate';
import getPayDetailModel from '@/modules/people/store/employment/get-pay-details-model';
import {
  AWARDS_TYPE_ENUM,
  EMPLOYMENT_TYPE,
  SALARY_DISTRIBUTION_ENUM,
} from '@/modules/people/definitions/pay-rates';
import useRootStore from '@/modules/people/store/root';
import {
  MODULE_TABS,
  MODULE_TABS_SUBTABS_ROUTES,
  PAY_RULE_ENUM,
  PRONOUNS_CUSTOM_ID,
  PRONOUNS_NOT_SPECIFIED_ID,
} from '@/modules/people/definitions';
import getPayCentres from '@/lib/request/webapp/people/pay-centres-get';
import getPayPeriods from '@/lib/request/webapp/people/pay-periods-get';
import areasGet from '@/lib/request/webapp/people/areas-get';
import useWorkDetailsStore, {
  WorkDetailsStoreStateLocation,
} from '@webapp-next/modules/people/store/work-detail';
import usePeopleStore from '@/modules/people/store';
import { isEnterprise } from '@/lib/company-subscription';
import { DateTime } from 'luxon';
import userHasPermission from '@/lib/user-has-permission';
import { checkPaySdpGeneral } from '@webapp-next/lib/check-pay-sensitive-data-permissions';
import useWorkingHoursStore from '@/modules/people/store/working-hours';
import useLeaveEntitlementStore from '@/modules/people/store/leave-entitlement';
import {
  Department,
  Training,
  Workplace,
} from '@/modules/people/types/people-setup';
import {
  AreaBasePayV2,
  AreaBasePayV2AreaGroup,
  JuniorRate,
  PayDetail,
  PayDetailSalaryDistribuitionConfig,
  PayPeriod,
  PayRatesIndexedByEmploymentType,
  PayRule,
  SalaryPayRuleObject,
  WeekDays,
} from '@/modules/people/types/employment';
import { defineStore } from 'pinia';
import {
  ContractLevel,
  EmployeeAgreement,
  EmployeeAgreementPayPoint,
  EmployeeContract,
} from '@/modules/people/types/employee';
import { PayRuleOverrideMap } from '@/modules/people/modules/pay-rates/utils/pay-rule';
import { sortObjAlphabeticallyIgnoringCase } from '@/modules/people/composables/sort';
import { EmployeeSave } from '@/lib/request/webapp/people/employee-save';
import useLeaveStandardHoursStore from '@/modules/people/store/leave-standard-hours';
import patchEndEmployment from '@/lib/request/webapp/people/employee-end-employment';
import { fromJSDateToISOString } from '@/modules/people/utils/date-converter';
import { PayCalendar } from '@/apps/payroll/src/modules/pay-calendar/types';
import { getPayCenterConfiguration } from '@webapp-next/modules/global-settings/composables/global-paycentres-config';
import {
  EVENT_NAME,
  EVENT_SCREEN,
  EVENT_SOURCE,
  PROTO,
  trackAnalyticsEvent,
} from '@apps-payroll/composables/analytics';
import { checkPayrollAdvancedPermissionEnabled } from '@apps-payroll/utils/feature-flags';
import getHrEligibilityCheck from '@/lib/request/webapp/people/hr/hr-eligibility-check-api';
import {
  checkEndEmploymentFF,
  checkJobTitlesFF,
} from '@/modules/people/utils/people-feature-flags';
import { getAreaBasedPayRequestObject } from '@/modules/people/composables/pay-rates';
import { initializeJuniorRateState } from '@/modules/people/utils/initialize-junior-rate-state';
import checkFF from '@webapp-next/lib/feature-flag';
import getPayrollEligibilityCheck from '@apps-payroll/lib/request/webapp/payroll-eligibility-check';
import { isAUProvider } from '@apps-payroll/utils/check-providers';
import useDeaStore from '@/modules/people/store/date-effective-agreements';
import { getIsDeaEnabled } from '../date-effective-agreements/dea-utils';
import { checkPayrollPermissions } from '@apps-payroll/utils/check-payroll-permissions';

export type FormTab = 'work' | 'pay' | 'hours' | 'leave';
export type FormSection =
  | 'workinghour'
  | 'employment'
  | 'employmentOther'
  | 'jobinfo'
  | 'general';

interface EmploymentEnterpriseRequestPayloadWorkplace {
  EmployeeId: number;
  Company: number;
  Agreement1: number;
  Agreement2: number;
  Agreement3: number;
  SortOrder: number;
}

export interface StateEmploymentEmployeeAgreement extends EmployeeAgreement {
  EmploymentDetails: Record<string, unknown>;
}

interface EmploymentEnterpriseRequestPayload {
  agreements: Partial<StateEmploymentEmployeeAgreement>[];
  workplaces: Partial<EmploymentEnterpriseRequestPayloadWorkplace>[];
}

export interface StateEmploymentDepartment extends Department {
  link: number;
  value: string;
  areaColor: string;
}

export interface StateEmploymentAreasForMultiSelector {
  link: number;
  value: string;
  areaColor?: string;
}

export type StateEmploymentWorkplace = {
  link: Workplace['Id'];
  value: Workplace['CompanyName'];
  label?: Workplace['CompanyName'];
};

export interface StateEmploymentTraining extends Training {
  link: number;
  value: string;
}

export interface StatePayDetailSalaryState {
  salaryPeriod: string;
  salaryAmount: number | null | undefined;
  salaryCostAllocation: number; // SALARY_DISTRIBUTION_ENUM.TYPE.NO_DISTRIBUTION.Id;
  weekDaysSalaryAllocation: WeekDays[];
  areasSelected: StateEmploymentDepartment[];
  isScheduledHours: boolean;
  isScheduledDays: boolean;
  isAllocateToOptionVisible: boolean;
  allocateSalaryTo: number;
  isAreaSelectionVisible: boolean;
  isExported?: boolean;
  payrollCategory?: string;
}

export interface InitializePayDetailsArgs {
  employeeHourlyRate: number | undefined;
  employeePayrollId: string;
  employeeContract: Partial<EmployeeContract>;
  isEnterprise: boolean;
  firstAgreementId: number;
  employeePayCalendarId: number;
  payCentre: number;
  level: number | undefined;
  contractLevels: Array<ContractLevel>;
}

export interface StatePayDetail extends PayDetail {
  areaBasedPayV2: AreaBasePayV2;
  active: boolean;
  awardLabel?: string;
  payrollId: string | undefined;
  // The Payroll ID a server refusal was shown for; the refusal stays until the value changes.
  payrollIdServerErrorValue?: string;
  // The server's refusal message, put back on the field while the value is unchanged.
  payrollIdServerErrorMessage?: string;
  type: number | undefined;
  contractObject: EmployeeContract | undefined;
  payPeriod?: number;
  salaryPayRuleObject?: SalaryPayRuleObject;
  payPointObject?:
    | EmployeeAgreementPayPoint
    | Partial<EmployeeAgreementPayPoint>;
  payPeriodObject?: PayPeriod;
  salaryState: StatePayDetailSalaryState;
  index?: number;
  payCentre?: number;
  empType?: number;
  startDate?: string;
  employmentType?: string;
  isVisible?: any;
  renderKey?: number;
  isNewLocalPayDetail?: boolean;
  payCalendarId?: number | null;
  payCalendarObject?: PayCalendar | null;
  areasToAllocateSalary?: any;
  contractLevels: Array<ContractLevel>;
  isEmploymentTypeUnknown?: boolean;
  isToggled?: boolean;
  isSensitiveData?: boolean;
  juniorRateId?: number | null;
  juniorRates?: JuniorRate[];
  overrideWithAdultRate?: boolean | null;
  /** The agreement's pay calendar belongs to a business entity outside the actor's payroll access. */
  isPayCalendarOutOfScope?: boolean;
  employeeAge?: number | null;
  adultBaseRate?: number | null;
  // The contract's unreduced rate, restored when the classification is cleared.
  // Distinct from adultBaseRate, which tracks the loaded classification and is
  // rewritten on every level change.
  adultDefaultBaseRate?: number | null;
}

export interface AreaIndexed {
  areaName: string;
  areaColor: string;
  companyName: string;
  locationId: number;
}

type AgreementPair = {
  payCentre: number;
  payPeriod: number;
  agreementId: number;
  active: boolean;
};

type StateEmployment = {
  localPayDetail: StatePayDetail[]; // Contains user modified data
  payDetailsCardInitialised: boolean;
  payDetail: StatePayDetail[];
  payRatesIndexedByEmploymentType:
    | PayRatesIndexedByEmploymentType
    | Partial<PayRatesIndexedByEmploymentType>;
  dataMap: Record<string, unknown> | undefined; // TODO: remove this dataMap logic
  payDetailIndex: number;
  newPayDetail: StatePayDetail | Partial<StatePayDetail>;
  editPayDetail: StatePayDetail | Partial<StatePayDetail>;
  scrollToHoursTab: FormTab | null;
  usedLocations: null | number[];
  validationError: Record<string, unknown>;
  validationWarning: Record<string, unknown>;
  workplaceAreas: Department[];
  excludedLocations: Record<string, unknown>; // TODO: verify this declaration
  isEndEmploymentEnabled: boolean;

  // employee commons
  payPeriods: PayPeriod[];
  payCentres: Workplace[];
  activeAgreement: undefined | EmployeeAgreement;

  // ABP
  ABP: { count: number; latestPayRuleId: number };
  showPayDetailsList: boolean;
  selectedPayTitle: string;
  payRateName: string;

  showSensitiveData: boolean;
  allAgreementPairs: AgreementPair[];
};
const getTemporaryNewAgreementId = (index = 0, currentTime = Date.now()) =>
  -currentTime - index;
const useEmploymentStore = defineStore('webapp:people:employment', {
  state: (): StateEmployment => {
    return {
      localPayDetail: [], // Contains user modified data
      payDetailsCardInitialised: false,
      payDetail: [],
      payRatesIndexedByEmploymentType: {},
      dataMap: undefined,
      payDetailIndex: 0,
      newPayDetail: {},
      editPayDetail: {},
      scrollToHoursTab: null,
      usedLocations: null,
      validationError: {},
      validationWarning: {},
      workplaceAreas: [],
      excludedLocations: {},
      isEndEmploymentEnabled: false,

      // employee commons
      payPeriods: [],
      payCentres: [],
      activeAgreement: undefined,

      // ABP
      ABP: { count: 0, latestPayRuleId: 1 },
      showPayDetailsList: true,
      selectedPayTitle: '',
      payRateName: '',
      showSensitiveData: false,
      allAgreementPairs: [],
    };
  },
  getters: {
    renderKeyToIndexMap(state: StateEmployment): Map<number, number> {
      const map = new Map<number, number>();
      for (let i = 0; i < state.localPayDetail.length; i++) {
        const key = state.localPayDetail[i].renderKey;
        if (key !== undefined && !map.has(key)) {
          map.set(key, i);
        }
      }
      return map;
    },
    isMultiplePayDetails(state: StateEmployment): boolean {
      return state.localPayDetail.length > 1;
    },
    hasOutOfScopePayDetail(state: StateEmployment): boolean {
      return state.localPayDetail.some(
        payDetail => payDetail.isPayCalendarOutOfScope === true
      );
    },
    areas(): Department[] {
      const rootState = useRootStore();
      return rootState.activeAreas;
    },
    areaMap(): Record<string, StateEmploymentAreasForMultiSelector[]> {
      return this.areas.reduce(
        (map: Record<string, StateEmploymentAreasForMultiSelector[]>, area) => {
          if (!map[area.Company]) {
            map[area.Company] = [];
          }

          map[area.Company].push({
            link: area.Id,
            value: `${area.OperationalUnitName}`,
          });

          return map;
        },
        {}
      );
    },
    areaIndexed(): AreaIndexed[] {
      return this.areas.reduce((indexed, area) => {
        indexed[area.Id] = {
          areaName: area.OperationalUnitName,
          areaColor: area.Colour ? area.Colour : `#000`,
          companyName: area.CompanyName, // TODO: change companyName to locationName
          locationId: area.Company,
        };
        return indexed;
      }, [] as AreaIndexed[]);
    },
    availableLocations(employmentState): WorkDetailsStoreStateLocation[] {
      const workDetailState = useWorkDetailsStore();
      const usedLocationsSet = new Set(employmentState.usedLocations ?? []);
      return workDetailState.locations.filter(
        l => !usedLocationsSet.has(l.link)
      );
    },
    excludedLocationSet(): Set<string> {
      return new Set(Object.keys(this.excludedLocations));
    },
    locationIndexed(): Record<number, Workplace> {
      const rootState = useRootStore();
      return rootState.locationsIndexed;
    },
    listPayPeriod(employmentState): Array<{ value: number; label: string }> {
      return sortObjAlphabeticallyIgnoringCase(
        employmentState.payPeriods.map(payPeriod => {
          return { value: payPeriod.Id, label: payPeriod.Name };
        }),
        'label'
      );
    },
    listPayCentre(employmentState): Array<{ value: number; label: string }> {
      return sortObjAlphabeticallyIgnoringCase(
        employmentState.payCentres.map(payCentre => {
          return { value: payCentre.Id, label: payCentre.CompanyName };
        }),
        'label'
      );
    },
  },
  actions: {
    archivePayDetail(callback: () => void, index: number | null = null): void {
      const payDetailIndex = index ?? this.payDetailIndex;
      const payDetail = this.getLocalPayDetailByIndex(payDetailIndex);

      payDetail.active = false;
      this.updateLocalPayDetailAtIndex(payDetailIndex, payDetail);
      callback?.();
    },

    unarchivePayDetail(
      callback: () => void,
      index: number | null = null
    ): void {
      const payDetailIndex = index ?? this.payDetailIndex;
      const payDetail = this.getLocalPayDetailByIndex(payDetailIndex);
      const usedLocationsIndexedById = new Set(this.usedLocations);

      payDetail.active = true;
      payDetail.locations = payDetail.locations?.filter(location => {
        return !usedLocationsIndexedById.has(location.Id);
      });
      this.updateLocalPayDetailAtIndex(payDetailIndex, payDetail);
      callback?.();
    },

    /**
     * // PORTED FROM: frontend/vnext/src/modules/people/composables/people-form-section-pay-details.js
     * // TODO: remove (employmentState)
     *
     * Gets the necessary payDetail from employmentState
     * A negative payDetailIndex is used to get the newPayDetail object
     * @param {Boolean} getLocalPayDetail
     * @param {Number} payDetailIndex
     * @param {Object} employmentState
     * @return {Object}
     */
    getPayDetail(
      getLocalPayDetail = true,
      payDetailIndex = -1
    ): Partial<StatePayDetail> {
      // getLocalPayDetail is a boolean and when set to true it means that you want to
      //  get one of the pay detail objects directly from employmentState.localPayDetail
      // This is worth mentioning since sometimes a user will be editing a pay detail
      //  on the employment edit page and other times they'll edit from a separate
      //  modal that pops up when you click "Edit" on a pay detail (only appears when you
      //  have multiple pay details). In the latter case the user should actually edit
      //  the employmentState.editPayDetail object rather than employmentState.localPayDetail
      //  since they might choose not to save it.
      if (getLocalPayDetail) {
        // localPayDetail is the state of all the pay details that we can edit.
        // We also have employmentState.payDetail which is the source of truth for all the pay details
        //  which we do NOT edit. We use employmentState.payDetail to compare to employmentState.localPayDetail
        //  to see if any changes have been made.
        return this.localPayDetail[payDetailIndex];
      } else if (this.payDetailIndex < 0) {
        // newPayDetail is where we keep the state of the new pay detail that we are creating
        return this.newPayDetail;
      } else {
        // editPayDetail is where we keep the state of the pay detail that we are editing
        return this.editPayDetail;
      }
    },
    getPayDetailByIndex(index = 0): StatePayDetail {
      if (index === -1) {
        return this.newPayDetail as StatePayDetail;
      }

      return this.localPayDetail[index];
    },
    getPayDetailByRenderKey(renderKey: number): StatePayDetail | undefined {
      if (checkFF('pre-6114-employment-collapse')) {
        const index = this.renderKeyToIndexMap.get(renderKey);
        return index !== undefined ? this.localPayDetail[index] : undefined;
      }
      return this.localPayDetail.find(
        payDetail => payDetail.renderKey === renderKey
      );
    },
    getPayDetailIndexByRenderKey(renderKey: number): number {
      if (checkFF('pre-6114-employment-collapse')) {
        return this.renderKeyToIndexMap.get(renderKey) ?? -1;
      }
      return this.localPayDetail.findIndex(
        payDetail => payDetail.renderKey === renderKey
      );
    },
    getLocalPayDetailByIndex(index = 0): StatePayDetail {
      return this.localPayDetail[index];
    },
    setLocalPayDetailByIndex(index = 0, payDetail: StatePayDetail): void {
      this.localPayDetail[index] = payDetail || {};
    },
    removeLocalPayDetailByIndex(index = 0): void {
      this.localPayDetail.splice(index, 1);
    },
    updateLocalPayDetailAtIndex(
      index: number,
      newPayDetail: StatePayDetail
    ): void {
      this.localPayDetail.splice(index, 1, newPayDetail);
    },
    upsertLocalPayDetailByIndex(
      index: number | undefined,
      payDetail: StatePayDetail
    ): void {
      payDetail.renderKey = new Date().getTime();
      const localIndex = index ?? this.payDetailIndex;
      const localPayDetail = payDetail || {};

      if (localIndex < 0 && this.newPayDetail) {
        localPayDetail.isNewLocalPayDetail = true;
        this.localPayDetail.push(localPayDetail);
      } else if (this.editPayDetail) {
        this.updateLocalPayDetailAtIndex(localIndex, localPayDetail);
      }
    },
    setLocalPayDetailActiveByIndex(index = 0, active = true): void {
      this.localPayDetail[index].active = active;
    },
    getLocalPayDetailsActive(): StatePayDetail[] {
      return this.localPayDetail.filter(({ active }) => active);
    },
    setLocalPayDetails(payDetails: StatePayDetail[] = []): void {
      this.localPayDetail = payDetails;
    },
    setAllAgreementPairs(pairs: AgreementPair[]): void {
      this.allAgreementPairs = pairs;
    },
    getPayDetailByAgreementId(id: number): StatePayDetail | null {
      return (
        this.getPayDetails().find(payDetail => payDetail.agreementId === id) ||
        null
      );
    },
    setActiveAgreement(agreement: EmployeeAgreement | undefined): void {
      if (agreement) {
        this.activeAgreement = agreement;
      }
    },
    getActiveAgreement(): EmployeeAgreement | undefined {
      return this.activeAgreement || undefined;
    },
    setPayDetailsIndex(index = 0): void {
      this.payDetailIndex = index;
    },
    setPayDetailContractByIndex(index = 0, contract: number): void {
      if (!this.payDetail[index]) {
        this.setPayDetail(index, getPayDetailModel());
      }

      this.payDetail[index].contract = contract;
    },
    addPayDetailByIndex(): void {
      this.editPayDetailByIndex(-1);
    },
    editPayDetailByIndex(index = 0): void {
      const rootState = useRootStore();
      this.setPayDetailsIndex(index);

      rootState.moduleNav({
        module: MODULE_TABS.EMPLOYMENT,
        edit: true,
        subModule: MODULE_TABS_SUBTABS_ROUTES.EMPLOYMENT.PAYDETAILS,
      });
    },
    fetchPayRatesIndexedByEmploymentType(): Partial<
      PayRatesIndexedByEmploymentType
    > {
      this.setPayRatesIndexedByEmploymentType(
        getAwardListByEmploymentType(getAllAwards())
      );

      return this.getPayRatesIndexedByEmploymentType();
    },
    getPayRatesIndexedByEmploymentType(): Partial<
      PayRatesIndexedByEmploymentType
    > {
      return this.payRatesIndexedByEmploymentType;
    },
    setPayRatesIndexedByEmploymentType(
      payRatesIndexedByEmploymentType: Partial<PayRatesIndexedByEmploymentType>
    ): void {
      this.payRatesIndexedByEmploymentType = payRatesIndexedByEmploymentType;
    },
    toggleShowSensitiveData(): void {
      this.showSensitiveData = !this.showSensitiveData;
    },
    resetShowSensitiveData(): void {
      this.showSensitiveData = false;
    },
    setPayDetails(payDetails: StatePayDetail[] = []): void {
      this.payDetail = payDetails.sort((a, b) => {
        if (a.isDefault) {
          return -1;
        } else if (b.isDefault) {
          return 1;
        } else {
          return 0;
        }
      });
    },
    getPayDetails(): StatePayDetail[] {
      return this.payDetail;
    },
    setPayDetail(index = 0, payDetail: StatePayDetail): void {
      if (index < 0) {
        this.newPayDetail = payDetail || {};
        return;
      }

      this.editPayDetail = payDetail;

      this.localPayDetail[index] = payDetail;
    },
    getLocalPayDetails(): StatePayDetail[] {
      return this.localPayDetail || [];
    },
    getLocalPayDetailsCount(): number {
      return Object.keys(this.localPayDetail)?.length || 0;
    },
    getDefaultPayDetail(): StatePayDetail | undefined {
      return this.getLocalPayDetails().find(payDetail => payDetail.isDefault);
    },
    setDefaultPayDetailByIndex(index = 0): void {
      this.localPayDetail = this.localPayDetail.map((payDetailItem, i) => {
        payDetailItem.isDefault = i === index;
        return payDetailItem;
      });
    },
    setExcludedLocations(locations: Record<string, unknown> = {}): void {
      this.excludedLocations = locations;
      this.fixABPAreas();
    },
    initializePayDetails({
      employeeHourlyRate,
      employeePayrollId = '',
      employeeContract = {},
      isEnterprise = false,
      firstAgreementId,
      employeePayCalendarId,
      payCentre,
      level,
      contractLevels,
    }: InitializePayDetailsArgs): void {
      this.setPayDetails();
      this.payRatesIndexedByEmploymentType = getAwardListByEmploymentType(
        getAllAwards()
      );
      const isNewPayDetailsUIEnabled = checkFF(
        'prf-256-new-ui-employee-pay-details'
      );
      const isMultiPayDetailsAvailableOnPremium = getPayCenterConfiguration().isMultiPayDetailsAvailableOnPremium();
      const singlePayDetail =
        !isEnterprise && !isMultiPayDetailsAvailableOnPremium;

      const payDetailOverride =
        !isEnterprise || !isMultiPayDetailsAvailableOnPremium
          ? { isPayRateAssigned: true }
          : {};
      const statePayDetail = getPayDetailModel(payDetailOverride);

      statePayDetail.type =
        employeeContract?.EmploymentStatus || EMPLOYMENT_TYPE.FULL_TIME;
      statePayDetail.isEmploymentTypeUnknown = !employeeContract?.EmploymentStatus;
      statePayDetail.contract = initializeEmployeeContract(employeeContract);
      statePayDetail.payRateType = getPayRateType(employeeContract);
      statePayDetail.basePayRule = getBasePayRule(employeeContract) || {};
      statePayDetail.defaultBaseRate = employeeContract.DefaultBaseRate;
      // The pay rule still holds the contract's own rate here; the block below
      // replaces it with the saved rate, so keep the unreduced copy first.
      statePayDetail.adultDefaultBaseRate =
        employeeContract.AdultDefaultBaseRate ??
        statePayDetail.basePayRule?.HourlyRate ??
        null;

      if (employeeHourlyRate && statePayDetail.basePayRule) {
        statePayDetail.basePayRule.HourlyRate = employeeHourlyRate;
      }

      statePayDetail.payRules = employeeContract
        ? getSortedPayRules(
            getPayRules(employeeContract),
            statePayDetail.payRateType
          )
        : getBlankRates(statePayDetail.payRateType);

      statePayDetail.thirdPartySync = employeeContract?.ThirdPartySync;
      statePayDetail.payrollId = employeePayrollId;
      statePayDetail.payCalendarId = employeePayCalendarId;
      if (!isEnterprise || !isMultiPayDetailsAvailableOnPremium) {
        /* With the premium option, there is only one payDetail, so we set the agreementId to the firstAgreementId and renderKey to 0 */
        statePayDetail.agreementId = firstAgreementId;
        statePayDetail.renderKey = 0;
        statePayDetail.payCentre = payCentre;
      }
      statePayDetail.level = level;
      statePayDetail.contractLevels = contractLevels;

      if (
        isNewPayDetailsUIEnabled &&
        singlePayDetail &&
        (statePayDetail.payRateType === AWARDS_TYPE_ENUM.SALARY.Code ||
          statePayDetail.payRateType === AWARDS_TYPE_ENUM.SALARY_CONTRACT.Code)
      ) {
        statePayDetail.salaryState = this.getSalaryState(statePayDetail);
      }

      this.setPayDetails([statePayDetail]);
      this.setLocalPayDetails(this.payDetail);
    },

    /**
     * Single-agreement tenant whose one pay detail sits on a pay calendar outside the actor's
     * business-entity access (CUE-4981). The edit form hides the whole Pay section, so the pay
     * detail editor and the salary editor never mount and never run initializePayDetails. The save
     * still posts the pay detail in full, because an omitted pay payload defaults to hourly on the
     * server and would rewrite a salaried employee. So hydrate it here from stored data exactly
     * as the editor would, keep the out-of-scope mark that hides the block, and fill the salary
     * state the salary editor would otherwise have built on mount.
     */
    initializeOutOfScopePayDetails(args: InitializePayDetailsArgs): void {
      this.initializePayDetails(args);

      const statePayDetail = this.localPayDetail[0];
      if (!statePayDetail) {
        return;
      }
      statePayDetail.isPayCalendarOutOfScope = true;

      const peopleState = usePeopleStore();
      const isSalaryType =
        statePayDetail.payRateType === AWARDS_TYPE_ENUM.SALARY.Code ||
        statePayDetail.payRateType === AWARDS_TYPE_ENUM.SALARY_CONTRACT.Code;
      if (isSalaryType) {
        // The salary editor resolves the allocation areas from the agreement when the stored
        // config has none; without them the save posts an empty area list.
        if (!statePayDetail.salaryDistributionConfig) {
          statePayDetail.salaryDistributionConfig =
            peopleState.employee.SalaryDistributionConfig;
        }
        const salaryConfig = statePayDetail.salaryDistributionConfig;
        if (
          salaryConfig?.areas === undefined &&
          salaryConfig?.agreement_id_for_areas_assoc
        ) {
          const rootState = useRootStore();
          const agreement = peopleState.getEmployeeAgreementsById(
            salaryConfig.agreement_id_for_areas_assoc
          );
          salaryConfig.areas = (agreement?.SalaryDistributions || []).map(
            areaId =>
              ({
                Id: areaId,
                OperationalUnitName:
                  rootState.getAreaById(areaId)?.OperationalUnitName || '',
              } as Department)
          );
        }
        statePayDetail.salaryState = this.getSalaryState(statePayDetail);
      }

      // The pay detail editor also builds the area-based pay state on mount. Without it the save
      // posts plain hourly rates and the server drops the employee's area rates, so build the
      // same state here under the same conditions the editor uses. The stored area groups are
      // kept as they are: nothing here is edited, and the Work details locations the editor
      // filters them against are not loaded yet at this point.
      const isAreaBasedPayV2Enabled =
        checkFF('cjs-422-area-based-pay-v2') &&
        statePayDetail.payRateType !== AWARDS_TYPE_ENUM.SALARY.Code &&
        statePayDetail.payRateType !== AWARDS_TYPE_ENUM.EXTERNAL.Code;
      if (
        isAreaBasedPayV2Enabled &&
        !getPayCenterConfiguration().isMPCAvailableOnPremium() &&
        !statePayDetail.areaBasedPayV2.initialized
      ) {
        this.setABPByPayDetail(statePayDetail);
        statePayDetail.areaBasedPayV2.initialized = true;

        // An archived area arrives as 0 inside its group and resolves to nothing; the editor
        // drops those in fixABPAreas. Do the same here, without the Work details filter.
        const { areaBasedPayV2 } = statePayDetail;
        areaBasedPayV2.areaGroups.forEach(areaGroup => {
          areaGroup.areas = (areaGroup.areas || []).filter(Boolean);
        });
        areaBasedPayV2.areaGroups = areaBasedPayV2.areaGroups.filter(
          areaGroup => areaGroup.areas.length > 0
        );
        areaBasedPayV2.areaSelected = areaBasedPayV2.areaGroups.flatMap(
          areaGroup => areaGroup.areas
        );

        if (areaBasedPayV2.areaGroups.length) {
          areaBasedPayV2.dataMap = getAreaBasedPayRequestObject(
            statePayDetail.payRateType,
            areaBasedPayV2.areaGroups,
            statePayDetail.contract,
            0
          );
        }
      }

      // The library rules editor (library awards and salary contracts) also reads the adult-rate
      // override off the agreement on mount; the model default would otherwise be posted and
      // undo a junior employee's override.
      if (
        checkFF('pre-6088-override-junior-rate') &&
        (statePayDetail.payRateType === AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code ||
          statePayDetail.payRateType === AWARDS_TYPE_ENUM.SALARY_CONTRACT.Code)
      ) {
        const agreement = peopleState.getEmployeeAgreementsById(
          statePayDetail.agreementId
        );
        if (agreement) {
          const contractObject = agreement.ContractObject;
          const contractPayRules = (contractObject?.PayRuleArray || []) as Array<
            Partial<PayRule>
          >;
          const originalBasePayRule = contractPayRules.find(
            payRule =>
              payRule.Id === contractObject?.BasePayRule ||
              payRule.Id === contractObject?.BasePayRuleObject?.Id
          );
          initializeJuniorRateState(statePayDetail, {
            juniorRates: contractObject?.DeputecEmploymentContractJuniorRates,
            baseRate:
              originalBasePayRule?.HourlyRate ??
              statePayDetail.basePayRule?.HourlyRate,
            dob: peopleState.employee?.DateOfBirth,
            juniorRateId: agreement.JuniorRateId,
            overrideWithAdultRate: agreement.OverrideWithAdultRate,
          });
        }
      }
      this.initializePayRulesWithOverrides();
    },

    async fetchPayCentresAndPayPeriods(): Promise<void> {
      const isPayCentresCached = this.payCentres.length > 0;
      const isPayPeriodsCached = this.payPeriods.length > 0;

      const [payCentres, payPeriods] = await Promise.all([
        !isPayCentresCached ? getPayCentres() : undefined,
        !isPayPeriodsCached ? getPayPeriods() : undefined,
      ]);

      if (!isPayCentresCached && payCentres) {
        this.setPayCentres(payCentres.data);
      }

      if (!isPayPeriodsCached && payPeriods) {
        // TODO: ignored till getPayCentres / getPayPeriods migration to TS
        this.setPayPeriods(payPeriods.data as PayPeriod[]);
      }
    },

    getSelectedAreas(
      statePayDetail: StatePayDetail
    ): StateEmploymentDepartment[] {
      return (
        statePayDetail.salaryDistributionConfig?.areas?.map(
          (item: StateEmploymentDepartment) => ({
            link: item.Id,
            value: item.OperationalUnitName,
          })
        ) || []
      );
    },

    getAllocateToOption(
      isAllocateToOptionVisible: boolean,
      areasSelected: StateEmploymentDepartment[]
    ): number {
      if (isAllocateToOptionVisible && areasSelected.length > 0) {
        return SALARY_DISTRIBUTION_ENUM.ALLOCATION_OPTIONS
          .OPTION_SPECIFIC_AREAS;
      }

      return SALARY_DISTRIBUTION_ENUM.ALLOCATION_OPTIONS.OPTION_SCHEDULED_AREAS;
    },

    getSalaryState(statePayDetail: StatePayDetail): StatePayDetailSalaryState {
      const peopleState = usePeopleStore();

      if (!statePayDetail.salaryDistributionConfig) {
        statePayDetail.salaryDistributionConfig =
          peopleState.employee.SalaryDistributionConfig;
      }
      const config = statePayDetail.salaryDistributionConfig;
      let salaryPeriod;
      let salaryAmount;

      if (config?.weekly_salary) {
        salaryPeriod = SALARY_DISTRIBUTION_ENUM.PERIOD.WEEKLY.Key;
        salaryAmount = config?.weekly_salary;
      } else {
        salaryPeriod = SALARY_DISTRIBUTION_ENUM.PERIOD.ANNUAL.Key;
        salaryAmount =
          statePayDetail.payRateType === AWARDS_TYPE_ENUM.SALARY_CONTRACT.Code
            ? peopleState.getEmployeeAgreementsByIndex(0)?.AnnualSalary || 0
            : statePayDetail.basePayRule?.AnnualSalary;
      }

      const salaryCostAllocation =
        config?.distribution_type !== undefined
          ? parseInt(config?.distribution_type)
          : SALARY_DISTRIBUTION_ENUM.TYPE.NO_DISTRIBUTION.Id;

      const isScheduledHours =
        salaryCostAllocation ===
        SALARY_DISTRIBUTION_ENUM.TYPE.SCHEDULED_HOURS.Id;

      const isScheduledDays =
        salaryCostAllocation ===
        SALARY_DISTRIBUTION_ENUM.TYPE.SCHEDULED_DAYS.Id;

      const isAllocateToOptionVisible = isScheduledHours || isScheduledDays;

      const areasSelected = this.getSelectedAreas(statePayDetail);

      const allocateSalaryTo = this.getAllocateToOption(
        isAllocateToOptionVisible,
        areasSelected
      );

      const isAreaSelectionVisible =
        salaryCostAllocation == SALARY_DISTRIBUTION_ENUM.TYPE.DAYS_OF_WEEK.Id ||
        (isAllocateToOptionVisible &&
          allocateSalaryTo ==
            SALARY_DISTRIBUTION_ENUM.ALLOCATION_OPTIONS.OPTION_SPECIFIC_AREAS);

      return {
        salaryPeriod,
        salaryAmount,
        salaryCostAllocation,
        weekDaysSalaryAllocation: getSelectedDays(
          statePayDetail.salaryDistributionConfig?.days_of_week
        ),
        areasSelected,
        isScheduledHours,
        isScheduledDays,
        isAllocateToOptionVisible,
        allocateSalaryTo,
        isAreaSelectionVisible,
      };
    },

    getPayPeriod(index = 0): PayPeriod {
      return this.payPeriods[index] || {};
    },
    setPayPeriods(payPeriods: PayPeriod[] = []): void {
      this.payPeriods = payPeriods;
    },
    getPayCentre(index = 0): Workplace {
      return this.payCentres[index] || {};
    },
    setPayCentres(payCentres: Workplace[] = []): void {
      this.payCentres = payCentres;
    },
    getPayCentres(): Workplace[] {
      return this.payCentres || [];
    },
    setScrollToHoursTab(tab: FormTab): void {
      this.scrollToHoursTab = tab;
    },
    async fetchAreas(employeeWorkplaceIds: number[]): Promise<void> {
      try {
        const { data } = await areasGet(employeeWorkplaceIds, [
          'TrainingModule',
        ]);

        // TODO: ignored till areasGet migration to TS
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.setWorkplaceAreas(data);
      } catch (e) {
        console.error(e);
      }
    },
    setWorkplaceAreas(locationAreas: Department[] = []): void {
      this.workplaceAreas = locationAreas;
    },
    primaryLocationValidation(): void {
      const workDetailsState = useWorkDetailsStore();

      const primaryLocation = workDetailsState.getLocationByIndex() as StateEmploymentWorkplace;
      const primaryLocationId = primaryLocation.link;

      let primaryLocationFound = false;
      for (const payDetail of this.getLocalPayDetails()) {
        const payDetailLocationIds = payDetail.locations.map(x => x.Id);

        if (payDetailLocationIds.includes(primaryLocationId)) {
          primaryLocationFound = true;
          break;
        }
      }

      if (!primaryLocationFound) {
        this.validationWarning['PayDetails'] = primaryLocation.label;
      } else {
        delete this.validationWarning['PayDetails'];
      }
    },
    getABPAreas(
      payDetail: StatePayDetail,
      useWorkdetailLocations = false
    ): StateEmploymentAreasForMultiSelector[] {
      let locationIds: number[] = [];

      if (this.isMultiplePayDetails) {
        locationIds = payDetail.locations?.map(location => location.Id);
      } else {
        const workDetailsState = useWorkDetailsStore();
        const peopleState = usePeopleStore();
        locationIds = useWorkdetailLocations
          ? workDetailsState.getLocationIds()
          : peopleState.getEmployeeLocationIds();
      }

      const employeeLocations = locationIds
        .filter(id => !this.excludedLocations[id])
        .map(id => this.locationIndexed[id]);

      payDetail.areaBasedPayV2.areaIndexed = {
        ...this.areaIndexed,
      };

      const result = employeeLocations
        .map(company => this.areaMap[company?.Id ?? ''] ?? [])
        .flat();
      return result;
    },
    initializePayRulesWithOverrides(): void {
      const peopleState = usePeopleStore();
      this.localPayDetail.forEach(payDetail => {
        const payRuleOverridesMap = peopleState.getEmployeePayRuleOverridesMap(
          payDetail.agreementId
        );

        if (!payDetail.payRules?.length) {
          /*
            Try to follow the initialization from Premium in the `initializePayDetails` function.
              In OneProduct MA Mode, payDetail is initialized only here,
              so we need to combine the enterprise and premium logic correctly.
           */
          payDetail.payRules = getBlankRates(payDetail.payRateType);
        } else if (payRuleOverridesMap) {
          applyPayRulesOverrides(payDetail.payRules, payRuleOverridesMap);
        }

        if (payDetail.areaBasedPayV2?.areaGroups?.length > 0) {
          payDetail.areaBasedPayV2.areaGroups.forEach(areaGroup => {
            const firstAreaIdInGroup = areaGroup.areas?.[0]?.link;
            const areaBasedPayRuleOverridesMap = peopleState.getAreaBasedPayRuleOverridesMap(
              firstAreaIdInGroup
            );
            if (areaGroup.payRules && areaBasedPayRuleOverridesMap) {
              applyPayRulesOverrides(
                areaGroup.payRules,
                areaBasedPayRuleOverridesMap
              );
            }
          });
        }
      });
    },
    setABPByPayDetail(payDetail: StatePayDetail): void {
      // Todo here we can setABPByPayDetail in both premium and enterprise
      const peopleState = usePeopleStore();
      const customPayData = peopleState.getCustomAreaBasedPayRates();
      const isMultiPayDetailsAvailableOnPremium = getPayCenterConfiguration().isMultiPayDetailsAvailableOnPremium();

      if (isEnterprise() || isMultiPayDetailsAvailableOnPremium) {
        const agreements = peopleState.getEmployeeAgreements();
        const useOptimized = checkFF('pre-6114-employment-collapse');
        const payDetailByAgreementId = useOptimized
          ? new Map(this.getPayDetails().map(pd => [pd.agreementId, pd]))
          : null;

        agreements.forEach((agreement: EmployeeAgreement) => {
          const payDetail = useOptimized
            ? payDetailByAgreementId!.get(agreement.Id) || null
            : this.getPayDetailByAgreementId(agreement.Id);

          const filteredPayData = filterAreaRatesByAreas(
            customPayData,
            agreement
          );

          if (!filteredPayData.areaRates.length) {
            filteredPayData.areaRates = [];
          }

          if (payDetail) {
            if (payDetail.contractObject) {
              if (isMultiPayDetailsAvailableOnPremium) {
                payDetail.areaBasedPayV2.areaList = this.getABPAreas(
                  payDetail,
                  true
                );
              }

              payDetail.areaBasedPayV2.areaGroups =
                getAreaBasedPayData({
                  contract: payDetail.contractObject,
                  payDetail,
                  customPayData: { ...filteredPayData },
                }) || [];
            } else {
              payDetail.areaBasedPayV2.areaGroups = [];
            }
          }
        });

        return;
      }

      payDetail.areaBasedPayV2.areaSelected = [];
      payDetail.areaBasedPayV2.validationError = {};
      payDetail.areaBasedPayV2.areaList = getAreaListForABP(payDetail);
      payDetail.areaBasedPayV2.areaGroups =
        getAreaBasedPayData({
          contract: peopleState.employee.Contract,
          payDetail,
          customPayData,
        }) || [];
    },
    getABPCount(): number {
      return this.ABP.count;
    },
    setABPCount(count = 0): void {
      this.ABP.count = count;
    },
    addABPCount(): number {
      this.setABPCount(this.ABP.count + 1);
      return this.getABPCount();
    },
    setABPLatestPayRuleId(id = 1): void {
      this.ABP.latestPayRuleId = id;
    },
    addABPLatestPayRuleId(): void {
      this.setABPLatestPayRuleId(this.ABP.latestPayRuleId + 1);
    },
    getABPBlankAreaGroupData(
      payDetail: StatePayDetail
    ): AreaBasePayV2AreaGroup {
      if (
        payDetail.payRateType === AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code
      ) {
        return {
          id: this.addABPCount(),
          areas: [],
          contract: '',
          basePayRule: null,
          payRules: [],
        };
      } else if (payDetail.payRateType === AWARDS_TYPE_ENUM.CUSTOM_TERM.Code) {
        return {
          id: this.addABPCount(),
          areas: [],
          contract: '',
          contractName: '',
          identifier: '',
          basePayRule: null,
          payRules: [],
        };
      } else if (
        payDetail.payRateType === AWARDS_TYPE_ENUM.RATES_PER_DAY.Code ||
        payDetail.payRateType === AWARDS_TYPE_ENUM.FIXED_RATE.Code
      ) {
        // used to use this function getRatesPerDayBlankRates
        return {
          id: this.addABPCount(),
          areas: [],
          payRules: payDetail.payRules?.map(payRule => {
            return { ...payRule, ...{ HourlyRate: 0 } };
          }),
        };
      } else if (payDetail.payRateType === AWARDS_TYPE_ENUM.HOURLY_44.Code) {
        // used to use this function getHourly44BlankRates
        return {
          id: this.addABPCount(),
          areas: [],
          payRules: payDetail.payRules?.map(payRule => {
            return {
              ...payRule,
              HourlyRate: payRule.IsMultiplier ? null : 0,
            };
          }),
        };
      } else {
        return {
          id: this.addABPCount(),
          areas: [],
          payRules: getBlankRates(payDetail.payRateType)?.map(payRule => {
            payRule.Id = this.addABPLatestPayRuleId();
            return payRule;
          }),
        };
      }
    },
    getABPFixAreasForEnterprise(payDetail: StatePayDetail): AreaBasePayV2 {
      payDetail = payDetail || {
        areaBasedPayV2: {},
      };
      payDetail.areaBasedPayV2.areaList = this.getABPAreas(payDetail, true);

      return this.getABPAdjustAreasGroups(payDetail).areaBasedPayV2;
    },

    getABPFixAreasForPremium(payDetail: StatePayDetail): AreaBasePayV2 {
      payDetail = payDetail || {
        areaBasedPayV2: {},
      };
      payDetail.areaBasedPayV2.areaList = getAreaListForABP(payDetail, true);
      return this.getABPAdjustAreasGroups(payDetail).areaBasedPayV2;
    },

    getABPAdjustAreasGroups(payDetail: StatePayDetail): StatePayDetail {
      payDetail = payDetail || {
        areaBasedPayV2: {},
      };

      const areaLinks = payDetail.areaBasedPayV2.areaList.map(
        area => area?.link
      );
      const availableAreaIds = checkFF('pre-6114-employment-collapse')
        ? new Set(areaLinks)
        : areaLinks;

      // TODO: this multiple loops can be eliminated if the area objects contains the location id
      payDetail.areaBasedPayV2.areaSelected = [];
      payDetail.areaBasedPayV2.areaGroups.forEach(areaGroup => {
        areaGroup.areas = areaGroup.areas.filter(area => {
          return availableAreaIds instanceof Set
            ? availableAreaIds.has(area?.link)
            : (availableAreaIds as (number | undefined)[]).includes(
                area?.link
              );
        });

        areaGroup.areas.forEach(area => {
          payDetail.areaBasedPayV2.areaSelected.push(area);
        });
      });

      return payDetail;
    },
    setABPAreaGroupByPayDetailIndex(index = 0, areaGroups = []): void {
      this.localPayDetail[index].areaBasedPayV2.areaGroups = areaGroups;
    },
    setABPByPayDetailIndex(index = 0, abp: AreaBasePayV2): void {
      const areaBasedPayV2 = {
        areaSelected: [],
        areaIndexed: {},
        areaList: [],
        areaGroups: [],
        validationError: {},
      };

      this.localPayDetail[index].areaBasedPayV2 = abp || areaBasedPayV2;
      this.payDetail[index].areaBasedPayV2 = abp || areaBasedPayV2;
    },
    getLocationIdsFromActivePayDetails(): Record<number, boolean> {
      return this.getLocalPayDetails().reduce(
        (reduced: Record<number, boolean>, payDetail) => {
          if (!payDetail.active) {
            return reduced;
          }

          payDetail.locations.forEach(
            location => (reduced[location.Id] = true)
          );
          return reduced;
        },
        {}
      );
    },
    getEmploymentEnterpriseRequestPayload(
      isCreatingDeaPayDetails = false
    ): EmploymentEnterpriseRequestPayload {
      const isMultiPayDetailsAvailableOnPremium = getPayCenterConfiguration().isMultiPayDetailsAvailableOnPremium();
      const workDetailsState = useWorkDetailsStore();
      const peopleState = usePeopleStore();
      const employeeId = peopleState.getEmployee().Id;
      const payDetails = this.localPayDetail;
      const worksAtLocations: StateEmploymentWorkplace[] = workDetailsState.getLocations();
      const usedLocationsIndexedById = this.getLocationIdsFromActivePayDetails();
      const worksAtLocationsIds = workDetailsState.getLocationIdsAsObject();

      const currentTime = Date.now();
      const { workplaces, agreements } = payDetails.reduce(
        (reduced: EmploymentEnterpriseRequestPayload, payDetail, index) => {
          if (!payDetail.active) {
            payDetail.locations = [...payDetail.locations].filter(
              location => !usedLocationsIndexedById[location.Id]
            );
          }

          if (!payDetail.active && !payDetail?.agreementId) {
            return reduced;
          }

          // subtract index value to be able to differentiate each agreement by Id when saving multiple pay details at once
          const temporaryNewAgreementId = getTemporaryNewAgreementId(
            index,
            currentTime
          );

          const tmpWorkplaces = payDetail.locations.reduce(
            (
              reduced: Partial<EmploymentEnterpriseRequestPayloadWorkplace>[],
              location
            ) => {
              if (worksAtLocationsIds[location.Id]) {
                reduced.push({
                  EmployeeId: employeeId,
                  Company: location.Id,
                  Agreement1: getIsDeaEnabled()
                    ? undefined
                    : payDetail.agreementId || temporaryNewAgreementId,
                });
              }
              return reduced;
            },
            []
          );

          reduced.workplaces.push(...tmpWorkplaces);

          const SalaryPayRuleObject = payDetail.salaryState
            ? {
                ...payDetail.salaryPayRuleObject,
                ...(payDetail.salaryState.salaryPeriod === 'AnnualSalary' && {
                  AnnualSalary: payDetail.salaryState.salaryAmount,
                }),
                ...(payDetail.salaryState.salaryPeriod ===
                  'PayPeriodSalary' && {
                  HourlyRate: payDetail.salaryState.salaryAmount,
                }),
                IsExported: payDetail.salaryState.isExported,
                PayrollCategory: payDetail.salaryState.isExported
                  ? payDetail.salaryState.payrollCategory
                  : '',
              }
            : {};

          let SalaryDistributionConfig = {} as Partial<
            PayDetailSalaryDistribuitionConfig
          >;
          if (payDetail.salaryState) {
            SalaryDistributionConfig = {
              ...payDetail.salaryDistributionConfig,
              pay_period_salary:
                payDetail.salaryState.salaryPeriod === 'PayPeriodSalary'
                  ? payDetail.salaryState.salaryAmount
                  : undefined,
              weekly_salary:
                payDetail.salaryState.salaryPeriod === 'WeeklySalary'
                  ? payDetail.salaryState.salaryAmount
                  : undefined,
              distribution_type: payDetail.salaryState.salaryCostAllocation,
              days_of_week: payDetail.salaryState.weekDaysSalaryAllocation?.map(
                day => {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  return SALARY_DISTRIBUTION_ENUM.DAY_OF_WEEK.DAY_TO_INT[day];
                }
              ),
              assoc_areas_for_scheduled:
                payDetail.salaryState.isAllocateToOptionVisible &&
                payDetail.salaryState.isAreaSelectionVisible
                  ? 1
                  : 0,
            };
            if (payDetail.salaryState.salaryPeriod === 'WeeklySalary') {
              SalaryDistributionConfig.weekly_salary =
                payDetail.salaryState.salaryAmount;
            }
          }

          const SalaryDistributions =
            payDetail.salaryState &&
            payDetail.salaryState.isAreaSelectionVisible
              ? payDetail?.salaryState.areasSelected.map(x => x.link)
              : [];

          payDetail.contractObject?.PayRuleArray?.forEach(payRule => {
            delete payRule?.DexmlScriptObject;
          });

          delete payDetail.basePayRule?.DexmlScriptObject;
          let employmentRequestData = this.getEmploymentRequestPayload(
            payDetail,
            index
          );

          if (!employmentRequestData && isMultiPayDetailsAvailableOnPremium) {
            // Assign default employee data when pay rate type is "No pay rate" on card view
            employmentRequestData = {
              PayRatesType: AWARDS_TYPE_ENUM.HOURLY.Id,
            };
          }

          reduced.agreements.push({
            Id: isCreatingDeaPayDetails
              ? temporaryNewAgreementId
              : payDetail.agreementId || temporaryNewAgreementId,
            EmployeeId: employeeId,
            PayPoint: payDetail.payCentre,
            CompanyIds: payDetail.locations?.map(location => location.Id) || [],
            // Currently empType never changes in new UI
            EmpType: payDetail.empType,
            Active: payDetail.active ? 1 : 0,
            StartDate: payDetail.startDate,
            Contract: payDetail.contract || 0,
            // TODO: Do we still need this?
            // SalaryPayRule: payDetail.salaryPayRule,
            PayrollId: payrollIdForSave(
              payDetail.payrollId,
              isPayrollIdValidationActive()
            ),
            PayPeriod: payDetail.payPeriod,

            ContractObject: {
              ...payDetail.contractObject,
              EmploymentStatus: payDetail.type,
              BasePayRuleObject: {
                ...payDetail.basePayRule,
              },
            },
            SalaryPayRuleObject,
            SalaryDistributionConfig,
            SalaryDistributions,
            PayPointObject: {
              ...payDetail.payPointObject,
            },
            PayPeriodObject: {
              ...payDetail.payPeriodObject,
            },

            CustomTermIdentifier: payDetail.identifier,
            IsCustomTermFromTemplate: Boolean(
              payDetail.contractName.match(/^CT/)
            ), // Search for isContractCustomEmploymentTermTemplate for legacy way of determining this (comes down to CT prefix)
            EmploymentDetails: { ...employmentRequestData },
            PayCalendarId: payDetail.payCalendarId,
            // Include JuniorRateId only when the contract has junior rates in play.
            // Each disjunct catches a different edit path: juniorRates.length covers
            // newly selected pay rates, overrideWithAdultRate covers a fresh override
            // toggle, and juniorRateId != null covers an existing agreement loaded
            // from the API. Keeping all three guards the field against silent drops
            // if any one signal is stale.
            ...(((payDetail.juniorRates?.length ?? 0) > 0 || payDetail.overrideWithAdultRate || payDetail.juniorRateId != null) && {
              JuniorRateId: payDetail.overrideWithAdultRate ? null : (payDetail.juniorRateId ?? null),
            }),
            // Only send the override when it is applicable (not null). A
            // no-junior contract does not show the checkbox and leaves it null,
            // so the field is omitted rather than persisted as false.
            ...(checkFF('pre-6088-override-junior-rate') &&
              payDetail.overrideWithAdultRate != null && {
                OverrideWithAdultRate: Boolean(payDetail.overrideWithAdultRate),
              }),
          });

          return reduced;
        },
        { workplaces: [], agreements: [] }
      );

      const workplacesIndexedByCompany = workplaces.reduce(
        (
          reduced: Record<
            number,
            Partial<EmploymentEnterpriseRequestPayloadWorkplace>
          >,
          workplace
        ) => {
          if (!workplace.Company) {
            return reduced;
          }

          const existingWorkplace = reduced[workplace.Company];

          if (existingWorkplace) {
            if (workplace.Agreement1 && !existingWorkplace.Agreement1) {
              existingWorkplace.Agreement1 = workplace.Agreement1;
            } else if (workplace.Agreement1 && !existingWorkplace.Agreement2) {
              existingWorkplace.Agreement2 = workplace.Agreement1;
            } else if (workplace.Agreement1 && !existingWorkplace.Agreement3) {
              existingWorkplace.Agreement3 = workplace.Agreement1;
            } else {
              console.error(
                'Error - Too many agreements assigned to a workplace'
              );
            }
          } else {
            reduced[workplace.Company] = workplace;
          }

          return reduced;
        },
        {}
      );

      const filteredWorksAtLocations = worksAtLocations.filter(location => {
        return !workplacesIndexedByCompany[location.link];
      });

      filteredWorksAtLocations.forEach(location => {
        workplacesIndexedByCompany[location.link] = {
          EmployeeId: employeeId,
          Company: location.link,
        };
      });

      // In order to change the company that the employee is associated with
      //    we must set the SortOrder to zero and every other location should
      //    have a unique positive number.
      const primaryLocationId = (workDetailsState.getLocationByIndex() as StateEmploymentWorkplace)
        .link;

      let count = 1;
      const sortedWorkplaces = Object.values(workplacesIndexedByCompany).map(
        workplace => {
          if (workplace.Company === primaryLocationId) {
            workplace.SortOrder = 0;
          } else {
            workplace.SortOrder = count++;
          }
          return workplace;
        }
      );

      return { workplaces: sortedWorkplaces, agreements };
    },

    // TODO: add test coverage here
    getEmploymentRequestPayload(
      givenPayDetail?: StatePayDetail,
      index?: number
    ):
      | RequestObject
      | RequestObjectCustomAward
      | RequestObjectCustomAwardV2
      | Record<string, unknown>
      | void {
      function getLibraryAwardPayLoad(payDetail: StatePayDetail) {
        const awardId = payDetail.contract;
        const basePayRule = payDetail.basePayRule;
        const payRules = payDetail.payRules;
        let payDetailIndex: number | undefined = 0;
        if (givenPayDetail) {
          payDetailIndex = payDetail.index || index;
        }
        return getRequestObjectLibraryAward(
          awardId,
          basePayRule,
          payRules,
          false,
          payDetailIndex,
          givenPayDetail?.renderKey
        );
      }

      const isMultiPayDetailsAvailableOnPremium = getPayCenterConfiguration().isMultiPayDetailsAvailableOnPremium();
      const payDetail = givenPayDetail || this.getLocalPayDetailByIndex();

      // TODO: Enterprise may contain multiple pay details
      const payRateType = payDetail.payRateType;
      const stateAreaBasedPay = payDetail.areaBasedPayV2;
      const isNewPayDetailsUIEnabled = checkFF(
        'prf-256-new-ui-employee-pay-details'
      );

      if (
        (isMultiPayDetailsAvailableOnPremium || isNewPayDetailsUIEnabled) &&
        stateAreaBasedPay.areaSelected.length &&
        stateAreaBasedPay.areaGroups.length &&
        !stateAreaBasedPay.dataMap
      ) {
        payDetail.areaBasedPayV2.dataMap = getAreaBasedPayRequestObject(
          payRateType,
          stateAreaBasedPay.areaGroups,
          payDetail.contract,
          index
        );
      }

      if (
        payRateType === AWARDS_TYPE_ENUM.HOURLY.Code ||
        (isMultiPayDetailsAvailableOnPremium &&
          payRateType === AWARDS_TYPE_ENUM.DEFAULT.Code)
      ) {
        return index !== undefined
          ? getRequestObjectHourly(payDetail.payRules, index)
          : getRequestObjectHourly(payDetail.payRules);
      }

      if (payRateType === AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code) {
        return getLibraryAwardPayLoad(payDetail);
      }

      if (
        payRateType ===
        AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Code
      ) {
        return index !== undefined
          ? getRequestObjectHourly40AndOvertime(payDetail.basePayRule, index)
          : getRequestObjectHourly40AndOvertime(payDetail.basePayRule);
      }

      if (payRateType === AWARDS_TYPE_ENUM.HOURLY_44.Code) {
        return index !== undefined
          ? getRequestObjectHourly44AndOvertime(
              payDetail.contract,
              payDetail.payRules,
              index
            )
          : getRequestObjectHourly44AndOvertime(
              payDetail.contract,
              payDetail.payRules
            );
      }

      if (payRateType === AWARDS_TYPE_ENUM.FIXED_RATE.Code) {
        return index !== undefined
          ? getRequestObjectFixedRate(
              payDetail.contract,
              payDetail.payRules,
              index
            )
          : getRequestObjectFixedRate(payDetail.contract, payDetail.payRules);
      }

      if (payRateType === AWARDS_TYPE_ENUM.CUSTOM_TERM.Code) {
        return getRequestObjectCustomAward(
          payDetail.contract,
          payDetail.contractName,
          payDetail.identifier,
          payDetail.basePayRule,
          payDetail.payRules
        );
      }

      if (payRateType === AWARDS_TYPE_ENUM.RATES_PER_DAY.Code) {
        return getRequestObjectRatesPerDay(
          payDetail.contract,
          payDetail.payRules,
          false,
          index
        );
      }

      if (payRateType === AWARDS_TYPE_ENUM.SALARY.Code) {
        return getSalaryRequestPayload(payDetail.salaryState);
      }

      if (payRateType === AWARDS_TYPE_ENUM.SALARY_CONTRACT.Code) {
        return {
          ...getLibraryAwardPayLoad(payDetail),
          ...getSalaryRequestPayload(payDetail.salaryState),
        };
      }

      if (payRateType === AWARDS_TYPE_ENUM.EXTERNAL.Code) {
        return getExternalRequestPayload(payDetail);
      }
    },
    getFormSectionsForSaving(): FormSection[] {
      const peopleState = usePeopleStore();
      const workDetailsState = useWorkDetailsStore();
      const workingHoursState = useWorkingHoursStore();

      const employee = peopleState.getEmployee();
      const sections: FormSection[] = [];

      if (
        employee.Id &&
        employee.FirstName &&
        employee.LastName &&
        employee.DisplayName &&
        workDetailsState.role &&
        workDetailsState.locations.length > 0
      ) {
        sections.push('general');
      }

      const canViewTrainingModules = userHasPermission(
        'Can_View_Training_Modules'
      );
      const isSupervisePermissionsEnabled = checkFF(
        'chip-2215-supervise-employee-permissions'
      );

      // With the flag on, the read returns no training to an editor without Can_View_Training_Modules.
      if (
        employee.StressProfile ||
        workDetailsState.getTraining().length ||
        (isSupervisePermissionsEnabled && !canViewTrainingModules)
      ) {
        sections.push('jobinfo');
      }

      if (
        workDetailsState.hiredOn ||
        workDetailsState.position ||
        // Not `&& jobTitleId`: clearing sets it to null, and the save handler
        // only applies employment-other fields when the section is present, so
        // gating on a value would make an unassign unsendable. `undefined` is
        // the one value that must not open the section — the read never
        // resolved a title, so there is nothing to write.
        (checkJobTitlesFF() && workDetailsState.jobTitleId !== undefined)
      ) {
        sections.push('employmentOther');
      }

      // TODO: Detect if pay rate has changed
      sections.push('employment');

      if (workingHoursState.agreedHour?.workPeriod?.cycle) {
        sections.push('workinghour');
      }

      return sections;
    },
    getEmployeeDataForSaving(isCreatingNewPayDetails = false): EmployeeSave {
      const isMPCAvailableOnPremium = getPayCenterConfiguration().isMPCAvailableOnPremium();
      const isMultiPayDetailsAvailableOnPremium = getPayCenterConfiguration().isMultiPayDetailsAvailableOnPremium();

      const rootState = useRootStore();
      const peopleState = usePeopleStore();
      const workDetailsState = useWorkDetailsStore();
      const workingHoursState = useWorkingHoursStore();
      const leaveEntitlementState = useLeaveEntitlementStore();
      const leaveStandardHoursState = useLeaveStandardHoursStore();
      const employee = peopleState.getEmployee();

      // TODO: localPayDetail is an array -- if enterprise. Payload should be updated.
      let employmentRequestData = null;
      let Workplaces: Workplace[] | null = null;
      let Agreements: Partial<StateEmploymentEmployeeAgreement>[] | null = null;

      const paySdp = checkPaySdpGeneral();
      const canManagePayDetails =
        userHasPermission('Can_Manage_Employee_PayDetails') &&
        paySdp.hasAccess;
      const payDetail = this.getLocalPayDetailByIndex();

      let Employment: Record<string, unknown> = {};
      if (canManagePayDetails) {
        Employment = {
          PayrollId: payrollIdForSave(
            payDetail.payrollId,
            isPayrollIdValidationActive()
          ),
          EmploymentStatus:
            payDetail.type || peopleState.getEmployeeEmploymentStatus(),
          PayCalendarId: payDetail.payCalendarId,
          PayPoint:
            isMPCAvailableOnPremium && !isMultiPayDetailsAvailableOnPremium
              ? payDetail.payCentre
              : undefined,
        };
      } else if (paySdp.shouldHide) {
        // Preserve existing pay values when pay SDP hides pay details,
        // so the backend doesn't overwrite them with nulls when saving
        // unrelated sections like leave entitlements.
        Employment = {
          PayrollId: peopleState.employee?.PayrollId,
          EmploymentStatus: peopleState.getEmployeeEmploymentStatus(),
          PayCalendarId:
            peopleState.getEmployeeAgreementsByIndex(0)?.PayCalendarId,
        };
      }

      if (isEnterprise() || isMultiPayDetailsAvailableOnPremium) {
        let {
          workplaces,
          agreements,
        } = this.getEmploymentEnterpriseRequestPayload(isCreatingNewPayDetails);

        if (!canManagePayDetails) {
          agreements = [];
        }

        Workplaces = workplaces.length ? (workplaces as Workplace[]) : null;
        Agreements = agreements.length ? agreements : null;
        employmentRequestData = canManagePayDetails
          ? this.getEmploymentRequestPayload() ?? {}
          : {};
      } else {
        employmentRequestData = canManagePayDetails
          ? this.getEmploymentRequestPayload() ?? {}
          : {};
      }
      const deaState = useDeaStore();

      if (getIsDeaEnabled()) {
        const effectiveDateLocal = isCreatingNewPayDetails
          ? deaState.createPayDetails.newEffectiveDateLocal
          : deaState.selectingEffectivePayDetails?.effectiveDateLocal || '';
        if (effectiveDateLocal) {
          const dt = DateTime.fromISO(effectiveDateLocal);
          (employmentRequestData as Record<
            string,
            any
          >).EffectiveDate = dt.toFormat('yyyy-MM-dd');
        }
      }
      if (!isEnterprise() && !isMultiPayDetailsAvailableOnPremium) {
        if (isCreatingNewPayDetails) {
          employmentRequestData.AgreementId = getTemporaryNewAgreementId();
        } else {
          const singlePremiumPayDetail = this.getLocalPayDetailByIndex(0);
          if (singlePremiumPayDetail) {
            employmentRequestData.AgreementId =
              singlePremiumPayDetail.agreementId;
          }
        }
      }
      const employmentEndDateAndReason = this.isEndEmploymentEnabled
        ? {
            EmploymentEndDate: fromJSDateToISOString(
              workDetailsState.employmentEndDate
            ),
            EmploymentEndReason: workDetailsState.employmentEndDate
              ? workDetailsState.employmentEndReason
              : '',
            EmploymentEndSentiment: workDetailsState.employmentEndSentiment
              ? workDetailsState.employmentEndSentiment
              : '',
            EmploymentEndComment: workDetailsState.employmentEndComment
              ? workDetailsState.employmentEndComment
              : '',
          }
        : {};

      let businessNumberAccessList = {};
      const isPayrollAdvancedPermissionEnabled = checkPayrollAdvancedPermissionEnabled();

      if (isPayrollAdvancedPermissionEnabled) {
        const roles = rootState.getRoles();
        const selectedRole = roles.find(
          role => role.Id === workDetailsState.role
        );

        if (selectedRole && selectedRole.ShowPayrollBusinessAccess) {
          businessNumberAccessList = {
            BusinessEntityAccessBusinessNumbers: workDetailsState.businessAccessList.map(
              b => b.id
            ),
          };
        } else {
          businessNumberAccessList = {
            BusinessEntityAccessBusinessNumbers: [],
          };
        }
      }

      return {
        // General
        Id: Number(peopleState.employee.Id), // no Id means new employee?
        FirstName: peopleState.employee.FirstName ?? '',
        LastName: peopleState.employee.LastName ?? '',
        DisplayName: peopleState.employee.DisplayName ?? '',
        Email: peopleState.employee.Email ?? '',
        Mobile: peopleState.employee.Mobile ?? '',
        Role: workDetailsState.role ?? null,
        WorkplaceIdArray: workDetailsState.locations?.map(
          (item: StateEmploymentWorkplace) => item.link
        ),
        // Job Info
        TrainingRecordArray: ((workDetailsState.trainingRecords || []) as Array<
          Training
        >).map(item => ({
          Id: item.Id,
          Module: item.Module,
          ExpiryDate: item.ExpiryDate,
          File: item.File,
          ModuleTitle: item.ModuleTitle,
          TrainingDate: item.TrainingDate,
          Comment: item.Comment,
          DownloadLink: item.DownloadLink,
        })),
        // Other
        StartDate: workDetailsState.hiredOn
          ? DateTime.fromJSDate(workDetailsState.hiredOn).toISODate()
          : null,
        ...employmentEndDateAndReason,
        Gender: peopleState.employee.Gender ?? PRONOUNS_NOT_SPECIFIED_ID,
        Pronouns: peopleState.employee.Pronouns ?? null,
        CustomPronouns:
          peopleState.employee.Pronouns === PRONOUNS_CUSTOM_ID
            ? peopleState.employee.CustomPronouns ?? null
            : '',
        DateOfBirth: peopleState.employee.DateOfBirth ?? null,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        AvgHoursPerDay:
          leaveStandardHoursState.getAvgHoursPerDay() ||
          employee.AvgHoursPerDay ||
          peopleState.employee?.AvgHoursPerDay,
        EntitledLeaveRule:
          leaveEntitlementState.entitledLeaveRules ||
          employee.EntitledLeaveRule,
        // Employment
        ...Employment,
        // Working Hours
        // TODO: workingHoursState contains StressProfile and AgreedHour
        ...workingHoursState.dataMap, // TODO: convert these dataMap to a more meaningful parameters
        StressProfile:
          workingHoursState.getStressProfile() ||
          peopleState.getEmployeeStressProfile(),
        ...this.dataMap, // TODO: convert these dataMap to a more meaningful parameters

        ...employmentRequestData,
        Position: workDetailsState.position,
        // Flag-gated so the flag-off payload stays byte-identical to before.
        // Inside the gate, null is a real value: clearing the field unassigns.
        // undefined is omitted entirely — sending it would read as an unassign.
        ...(checkJobTitlesFF() && workDetailsState.jobTitleId !== undefined
          ? { JobTitleId: workDetailsState.jobTitleId }
          : {}),
        // The backend reads each key's presence as intent, so an empty array deletes every row.
        ...(workDetailsState.prefersLoaded
          ? { Prefers: workDetailsState.prefers || [] }
          : {}),
        ...(workDetailsState.managesLoaded
          ? { Manages: workDetailsState.manages || [] }
          : {}),
        sections: this.getFormSectionsForSaving(),
        Agreements,
        Workplaces,
        ...businessNumberAccessList,
      };
    },
    // TODO: eliminate this dataMap object as it double up with the rest of the state
    setDataMap(dataMap = {}): void {
      this.dataMap = dataMap;
    },
    async editSave(isCreatingNewPayDetails = false): Promise<void> {
      const employee = this.getEmployeeDataForSaving(isCreatingNewPayDetails);
      const peopleState = usePeopleStore();

      if (isEnterprise()) {
        for (const payDetail of this.getLocalPayDetails()) {
          if (
            payDetail?.salaryState.isAreaSelectionVisible &&
            !payDetail?.salaryState.areasSelected.length
          ) {
            throw new Error('pay-section-salary-error-no-area-selected');
          }
        }
      }

      try {
        await peopleState.saveEmployee(employee);
      } catch (e) {
        console.error(e);
        throw e;
      }
    },
    async saveEndEmployment(employeeId: number): Promise<void> {
      const peopleState = usePeopleStore();
      const workDetailsState = useWorkDetailsStore();
      const patchData = this.isEndEmploymentEnabled
        ? {
            employmentEndDate: workDetailsState?.employmentEndDate,
            employmentEndReason: workDetailsState?.employmentEndReason,
            employmentEndSentiment: workDetailsState?.employmentEndSentiment,
            employmentEndComment: workDetailsState?.employmentEndComment,
          }
        : {};

      // If the employee has an end date already, it means "editing" the end employment
      const isEditing = Boolean(peopleState.employee?.EmploymentEndDate);
      const eventName = isEditing
        ? {
            request: EVENT_NAME.EMPLOYEE_SENTIMENT_UPDATE_REQUESTED,
            success: EVENT_NAME.EMPLOYEE_SENTIMENT_UPDATE_SUCCESS_ACKNOWLEDGED,
            failure: EVENT_NAME.EMPLOYEE_SENTIMENT_UPDATE_FAILURE_ACKNOWLEDGED,
          }
        : {
            request: EVENT_NAME.EMPLOYEE_SENTIMENT_CREATE_REQUESTED,
            success: EVENT_NAME.EMPLOYEE_SENTIMENT_CREATE_SUCCESS_ACKNOWLEDGED,
            failure: EVENT_NAME.EMPLOYEE_SENTIMENT_CREATE_FAILURE_ACKNOWLEDGED,
          };
      const eventSource = isEditing
        ? EVENT_SOURCE.SET_EMPLOYEE_SENTIMENT_EDIT
        : EVENT_SOURCE.SET_EMPLOYEE_SENTIMENT;

      trackAnalyticsEvent({
        name: eventName.request,
        screen: EVENT_SCREEN.PEOPLE,
        source: eventSource,
        proto: PROTO.EMPLOYEE_SENTIMENT,
        details: {
          reason: workDetailsState?.employmentEndReason,
          sentiment: workDetailsState?.employmentEndSentiment,
          comment: Boolean(workDetailsState?.employmentEndComment),
        },
      });

      try {
        const { success } = await patchEndEmployment(employeeId, patchData);
        if (success) {
          // Refresh employee caches so table rows (derived from searchResults) receive latest data
          await peopleState.fetchEmployeeById(employeeId);

          trackAnalyticsEvent({
            name: eventName.success,
            screen: EVENT_SCREEN.PEOPLE,
            source: eventSource,
            proto: PROTO.EMPLOYEE_SENTIMENT,
            details: {
              reason: workDetailsState?.employmentEndReason,
              sentiment: workDetailsState?.employmentEndSentiment,
              comment: Boolean(workDetailsState?.employmentEndComment),
            },
          });
        } else {
          console.error('Failed to save end employment');
          throw new Error('Failed to save end employment');
        }
      } catch (e) {
        console.error(e);

        trackAnalyticsEvent({
          name: eventName.failure,
          screen: EVENT_SCREEN.PEOPLE,
          source: eventSource,
          proto: PROTO.EMPLOYEE_SENTIMENT,
          details: {
            reason: workDetailsState?.employmentEndReason,
            sentiment: workDetailsState?.employmentEndSentiment,
            comment: Boolean(workDetailsState?.employmentEndComment),
            errorMessage: e,
          },
        });

        throw e;
      }
    },

    setUsedLocations(locationIds: number[] = []): void {
      this.usedLocations = locationIds;
    },
    // TODO: is this specific for enterprise?
    hidePayDetails(): void {
      this.showPayDetailsList = false;
    },
    showPayDetails(): void {
      this.showPayDetailsList = true;
    },
    // TODO: PRE-2793 this is a temp code for test purposes
    // TODO: this has a hidden side effect within the store in the areaBasedPayV2 object
    fixABPAreas(payDetail?: StatePayDetail): void {
      const fixABP =
        isEnterprise() || getPayCenterConfiguration().isMPCAvailableOnPremium()
          ? this.getABPFixAreasForEnterprise
          : this.getABPFixAreasForPremium;

      if (payDetail) {
        payDetail.areaBasedPayV2 = fixABP(payDetail);
        return;
      }

      this.getLocalPayDetails().forEach(
        payDetail => (payDetail.areaBasedPayV2 = fixABP(payDetail))
      );
    },
    shouldShowPayCenters(): boolean {
      return (
        getPayCenterConfiguration().isMPCAvailableOnPremium() || isEnterprise()
      );
    },
    resetInitializedPayDetailsState(): void {
      this.setPayDetailsCardInitialisedState(false);
    },
    setPayDetailsCardInitialisedState(initialisedState: boolean): void {
      this.payDetailsCardInitialised = initialisedState;
    },
    async fetchEndEmploymentEnabled(): Promise<void> {
      this.isEndEmploymentEnabled = await this.checkEndEmploymentEnabled();
    },
    async checkEndEmploymentEnabled(): Promise<boolean> {
      const { hasManagePayrollDetailsPermission } = checkPayrollPermissions();
      const {
        eligibility: payrollEligible,
        provider,
      } = await getPayrollEligibilityCheck();

      // The payroll-driven End Employment flow only applies to AU payroll
      // providers (Microkeeper). Paycor (US) tenants fall through to the
      // HR-only path below — see PAYU-626.
      if (
        hasManagePayrollDetailsPermission &&
        payrollEligible &&
        isAUProvider(provider)
      ) {
        return true;
      }

      const isFeatureFlagEnabled = checkEndEmploymentFF();
      if (!isFeatureFlagEnabled) {
        return false;
      }

      const {
        eligibility: { hr: isHrEnabled },
      } = await getHrEligibilityCheck({
        hr: true,
        ess: false,
        sentiment: false,
        docLibrary: false,
      });

      return isHrEnabled;
    },
  },
});

export function applyPayRulesOverrides(
  payRules: PayRule[],
  payRuleOverridesMap: PayRuleOverrideMap
): void {
  payRules.forEach(payRule => {
    const foundOverrideValue = payRuleOverridesMap[payRule.Id];
    const shouldOverrideValue =
      typeof foundOverrideValue === 'number' ||
      typeof foundOverrideValue === 'string';

    switch (payRule.RemunerationType) {
      case PAY_RULE_ENUM.REMUNERATION_TYPE.HOURLY.Id: {
        if (shouldOverrideValue) {
          payRule.HourlyRate = foundOverrideValue as number;
        }
        payRule.UnitValue = null;
        break;
      }
      case PAY_RULE_ENUM.REMUNERATION_TYPE.UNIT.Id: {
        if (shouldOverrideValue) {
          payRule.UnitValue = foundOverrideValue as string;
        }
        payRule.HourlyRate = null;
        break;
      }
      case PAY_RULE_ENUM.REMUNERATION_TYPE.MULTIPLE.Id: {
        if (shouldOverrideValue) {
          payRule.MultiplierValue = foundOverrideValue as number;
        }
        payRule.UnitValue = null;
        payRule.HourlyRate = null;
        break;
      }
      default: {
        break;
      }
    }
  });
}
export default useEmploymentStore;
